/**
 * @yada/logger
 *
 * Scoped, caller-based logging infrastructure with fine-grained debug control.
 *
 * Rules:
 * - All log outputs start with `[caller]`, e.g. `[openDocument]`.
 * - Debug logs (`logger.debug(caller, ...)` and `logger.log(caller, ...)`) are suppressed by default
 *   unless enabled for that caller.
 * - Callers can be enabled via:
 *   1. Cookie `debug_<caller>=true` (e.g. `debug_openDocument=true` or wildcard `debug_all=true`) — works without auth/permissions.
 *   2. Developer/Tester account settings: localStorage `yada:debug:callers` (JSON array or set) or `yada:debug:<caller>=true`.
 *   3. `logger.enableCaller(caller)` / `logger.disableCaller(caller)`.
 * - Errors and warnings (`logger.error(caller, ...)` and `logger.warn(caller, ...)`) are always outputted
 *   with prefix `[caller]`.
 */

export type LogLevel = 'debug' | 'log' | 'info' | 'warn' | 'error';

export interface KnownCallerGroup {
  name: string;
  callers: { id: string; name: string; description: string }[];
}

export const KNOWN_CALLERS: KnownCallerGroup[] = [
  {
    name: 'Storage & CRDT Sync',
    callers: [
      { id: 'openDocument', name: 'openDocument', description: 'Opening, resolving, and decrypting Yjs documents' },
      { id: 'documentSync', name: 'documentSync', description: 'Remote document syncing, merges, and auto-sync debouncing' },
      { id: 'CRDTStorage', name: 'CRDTStorage', description: 'CRDT-based storage adapter initialization and locking' },
      { id: 'OPFSPersistence', name: 'OPFSPersistence', description: 'Local OPFS caching and disk reads/writes' },
      { id: 'workspaceSeedManager', name: 'workspaceSeedManager', description: 'Workspace cryptographic seeds, relaying, and prefetching' },
      { id: 'reclaimSecurity', name: 'reclaimSecurity', description: 'Local-first state restoration and ownership verification' },
      { id: 'accountSettings', name: 'accountSettings', description: 'Hydration and cloud persistence of account settings' },
      { id: 'storageProvider', name: 'storageProvider', description: 'Selection and initialization of local and CRDT app storage' },
      { id: 'compactToRemote', name: 'compactToRemote', description: 'CRDT document compaction and conflict resolution' },
    ],
  },
  {
    name: 'Authentication & Devices',
    callers: [
      { id: 'AuthGuard', name: 'AuthGuard', description: 'Session validation, local-first boot, and state transitions' },
      { id: 'fetchWithAuth', name: 'fetchWithAuth', description: 'Authenticated API requests and token refreshes' },
      { id: 'Signaling', name: 'Signaling', description: 'WebSocket signaling and P2P direct exchange' },
      { id: 'deviceStorage', name: 'deviceStorage', description: 'Device keys and IndexedDB device identity' },
      { id: 'pairingSession', name: 'pairingSession', description: 'Device pairing, verification codes, and link handshakes' },
      { id: 'PendingDevicesContext', name: 'PendingDevicesContext', description: 'Pending device approval queue and polling' },
    ],
  },
  {
    name: 'Runtime, LLM & Services',
    callers: [
      { id: 'CustomRuntime', name: 'CustomRuntime', description: 'App manifest loading, mounting, and host API wiring' },
      { id: 'LlmManager', name: 'LlmManager', description: 'LLM model loading, prompts, and streaming' },
      { id: 'NotificationManager', name: 'NotificationManager', description: 'Service worker cues and notification scheduling' },
      { id: 'QueryRegistry', name: 'QueryRegistry', description: 'Cross-app read interface queries' },
      { id: 'WeatherService', name: 'WeatherService', description: 'Weather updates, geolocation, and Met Norway provider' },
    ],
  },
  {
    name: 'Apps & UI',
    callers: [
      { id: 'DocsStore', name: 'DocsStore', description: 'Docs app tree loading and note mutations' },
      { id: 'Editor', name: 'Editor', description: 'Document editor block operations, cursor, and focus' },
      { id: 'Chat', name: 'Chat', description: 'Chat app messaging, workspace sync, and AI features' },
      { id: 'ShoppingList', name: 'ShoppingList', description: 'Shopping list updates and AI categorization' },
      { id: 'Habits', name: 'Habits', description: 'Habit tracking and achievement evaluation engine' },
      { id: 'TodoStore', name: 'TodoStore', description: 'Todo app mutations and index saves' },
    ],
  },
];

const STORAGE_DEBUG_PREFIX = 'yada:debug:';
const STORAGE_CALLERS_KEY = 'yada:debug:callers';

/**
 * Cache for quick lookup during high-throughput logging.
 */
let enabledCallersCache: Set<string> | null = null;
let cacheInvalidated = true;

/**
 * Parses cookies in the current browser session.
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/**
 * Checks if a specific cookie is truthy (e.g. "true", "1", "yes").
 */
function isCookieEnabled(name: string): boolean {
  const val = getCookie(name);
  if (!val) return false;
  const s = val.trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'yes' || s === 'on';
}

/**
 * Reads enabled callers from localStorage.
 */
function loadEnabledCallersFromStorage(): Set<string> {
  const set = new Set<string>();
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return set;

  try {
    const raw = localStorage.getItem(STORAGE_CALLERS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (typeof item === 'string') set.add(item);
        }
      }
    }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_DEBUG_PREFIX) && key !== STORAGE_CALLERS_KEY) {
        const caller = key.slice(STORAGE_DEBUG_PREFIX.length);
        if (localStorage.getItem(key) === 'true') {
          set.add(caller);
        }
      }
    }
  } catch {
    // Ignore storage parse errors
  }

  return set;
}

/**
 * Invalidates local memory cache of enabled callers.
 */
export function invalidateLoggerCache(): void {
  cacheInvalidated = true;
  enabledCallersCache = null;
}

/**
 * Determines whether debug logs for a given caller should be emitted to console.
 */
export function isCallerDebugEnabled(caller: string): boolean {
  // 1. Check cookie overrides (debug_<caller>=true or debug_all=true)
  // Cookies bypass permission checks and work unauthenticated
  if (isCookieEnabled(`debug_${caller}`) || isCookieEnabled('debug_all')) {
    return true;
  }

  // 2. Check cached/stored settings
  if (cacheInvalidated || !enabledCallersCache) {
    enabledCallersCache = loadEnabledCallersFromStorage();
    cacheInvalidated = false;
  }

  return enabledCallersCache.has(caller) || enabledCallersCache.has('*') || enabledCallersCache.has('all');
}

/**
 * Enables or disables debug logging for a caller and saves to localStorage.
 */
export function setCallerDebugEnabled(caller: string, enabled: boolean): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;

  try {
    const callers = loadEnabledCallersFromStorage();
    if (enabled) {
      callers.add(caller);
      localStorage.setItem(`${STORAGE_DEBUG_PREFIX}${caller}`, 'true');
    } else {
      callers.delete(caller);
      localStorage.removeItem(`${STORAGE_DEBUG_PREFIX}${caller}`);
    }
    localStorage.setItem(STORAGE_CALLERS_KEY, JSON.stringify(Array.from(callers)));
    invalidateLoggerCache();
    window.dispatchEvent(new CustomEvent('yada:debug:callers-changed', { detail: { caller, enabled } }));
  } catch {
    // Ignore storage write errors
  }
}

/**
 * Gets the list of currently enabled debug callers from localStorage.
 */
export function getEnabledDebugCallers(): string[] {
  if (cacheInvalidated || !enabledCallersCache) {
    enabledCallersCache = loadEnabledCallersFromStorage();
    cacheInvalidated = false;
  }
  return Array.from(enabledCallersCache);
}

/**
 * Checks if the given account role / flags have developer or tester access.
 */
export function isDeveloperOrTester(account: { role?: string; isDeveloper?: boolean; isTestAccount?: boolean } | null | undefined): boolean {
  if (!account) return false;
  if (account.isDeveloper || account.isTestAccount) return true;
  const role = account.role;
  return role === 'developer' || role === 'tester' || role === 'admin';
}

/**
 * Formats caller prefix as `[caller]`.
 */
function formatCaller(caller: string): string {
  const clean = caller.trim().replace(/^\[+|\]+$/g, '');
  return `[${clean}]`;
}

/**
 * Logger core class
 */
export class ScopedLogger {
  constructor(private defaultCaller?: string) {}

  private resolveCaller(callerOrFirstArg?: string): { caller: string; skipArg: boolean } {
    if (this.defaultCaller) {
      return { caller: this.defaultCaller, skipArg: false };
    }
    if (typeof callerOrFirstArg === 'string' && callerOrFirstArg.length > 0) {
      return { caller: callerOrFirstArg, skipArg: true };
    }
    return { caller: 'app', skipArg: false };
  }

  debug(caller: string, ...args: any[]): void;
  debug(...args: any[]): void;
  debug(...args: any[]): void {
    const { caller, skipArg } = this.resolveCaller(args[0]);
    if (!isCallerDebugEnabled(caller)) return;
    const logArgs = skipArg ? args.slice(1) : args;
    console.debug(formatCaller(caller), ...logArgs);
  }

  log(caller: string, ...args: any[]): void;
  log(...args: any[]): void;
  log(...args: any[]): void {
    const { caller, skipArg } = this.resolveCaller(args[0]);
    if (!isCallerDebugEnabled(caller)) return;
    const logArgs = skipArg ? args.slice(1) : args;
    console.log(formatCaller(caller), ...logArgs);
  }

  info(caller: string, ...args: any[]): void;
  info(...args: any[]): void;
  info(...args: any[]): void {
    const { caller, skipArg } = this.resolveCaller(args[0]);
    if (!isCallerDebugEnabled(caller)) return;
    const logArgs = skipArg ? args.slice(1) : args;
    console.info(formatCaller(caller), ...logArgs);
  }

  warn(caller: string, ...args: any[]): void;
  warn(...args: any[]): void;
  warn(...args: any[]): void {
    const { caller, skipArg } = this.resolveCaller(args[0]);
    const logArgs = skipArg ? args.slice(1) : args;
    console.warn(formatCaller(caller), ...logArgs);
  }

  error(caller: string, ...args: any[]): void;
  error(...args: any[]): void;
  error(...args: any[]): void {
    const { caller, skipArg } = this.resolveCaller(args[0]);
    const logArgs = skipArg ? args.slice(1) : args;
    console.error(formatCaller(caller), ...logArgs);
  }

  createScope(caller: string): ScopedLogger {
    return new ScopedLogger(caller);
  }
}

export const logger = new ScopedLogger();

/**
 * Helper to create a dedicated logger for a specific module/caller.
 */
export function createLogger(caller: string): ScopedLogger {
  return new ScopedLogger(caller);
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key && (e.key.startsWith(STORAGE_DEBUG_PREFIX) || e.key === STORAGE_CALLERS_KEY)) {
      invalidateLoggerCache();
    }
  });
  window.addEventListener('yada:debug:callers-changed', () => {
    invalidateLoggerCache();
  });
}
